
import {Injectable} from '@angular/core';

import {fromEvent, merge, of, Subject, timer} from 'rxjs';
import {catchError, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from './../../destroyable.component';
import {ApiService} from './../../shared/api';
import {KeycloakWrapperService} from './../../shared/authentication/keycloak-wrapper.service';
import {ModalService} from './../../shared/modal';


@Injectable({
  providedIn: 'root'
})
export class IdleSessionService extends Destroyable {
  private idleTimeout = 480 * 60 * 1000; // 480 minutes in milliseconds
  private countdownDuration = 60 * 1000; // 60 seconds in milliseconds
  private idleSubject = new Subject<void>();
  private countdownSubject = new Subject<number>();
  private lastActivityTime: number = Date.now();

  constructor(
    private apiService: ApiService,
    private modalService: ModalService,
    private keycloakWrapper: KeycloakWrapperService
  ) {
    super();
    this.setupIdleDetection();
  }

  private setupIdleDetection(): void {
    let modalRef: any = null;

    this.apiService.get('/authent/api/v1/keycloak/config/sso-idle-timeout').pipe(
      tap((idleTime: number) => {
        if (idleTime && idleTime >= 120) {
          this.idleTimeout = idleTime * 1000;
        }
      }),
      catchError(() => of(this.idleTimeout)),
      switchMap(() => {
        const activityEvents = merge(
          fromEvent(document, 'mousemove'),
          fromEvent(document, 'keydown'),
          fromEvent(document, 'touchstart'),
          fromEvent(document, 'click'),
          fromEvent(document, 'scroll'),
          fromEvent(document, 'visibilitychange')
        );
        return activityEvents.pipe(
          startWith(new Event('init')),
          tap(() => {
            if (!modalRef) {
              this.lastActivityTime = Date.now();
            }
          }),
          switchMap(() => timer(0, 1000).pipe(
            takeUntil(activityEvents),
            takeUntil(this.componentDestroyed$)
          )),
          tap(() => {
            const elapsedTime = Date.now() - this.lastActivityTime;
            const remainingTime = Math.max(0, this.idleTimeout - elapsedTime);
            const remainingSeconds = Math.round(remainingTime / 1000);

            if (remainingTime <= 0) {
              this.keycloakWrapper.logout();
              this.idleSubject.next();
              if (modalRef) {
                this.modalService.closeTopmostModal();
                modalRef = null;
              }
            } else if (remainingTime <= this.countdownDuration && !modalRef) {
              this.modalService.openModalWithName('IdleTimeoutModalComponent').then((modal: any) => {
                modalRef = modal;
                modalRef.instance.remainingSeconds = remainingSeconds;
                modalRef.instance.logout = () => {
                  this.keycloakWrapper.logout();
                  this.idleSubject.next();
                  this.modalService.closeTopMostModalFromOutlet();
                  modalRef = null;
                };
                modalRef.instance.keepSession = () => {
                  this.modalService.closeTopMostModalFromOutlet();
                  modalRef = null;
                  this.lastActivityTime = Date.now();
                };
              });
            } else if (modalRef && remainingSeconds >= 0) {
              modalRef.instance.remainingSeconds = remainingSeconds;
              this.countdownSubject.next(remainingSeconds);
            }
          }),
          takeUntil(this.componentDestroyed$)
        );
      })
    ).subscribe();
  }

  public onIdle(): Subject<void> {
    return this.idleSubject;
  }
}
